/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  View,
  useWindowDimensions,
  ScrollView,
  GestureResponderEvent,
  Platform,
  ViewStyle,
} from 'react-native';
import StickyBottomBarView from '@airasia-phoenix/react-native/components/StickyBottomBarView';
import { StyleSheet } from 'react-native-unistyles';
import GuestSelectionView from './GuestSelectionView';
import BottomSheetWrapper from '../BottomSheetView/BottomSheetView';
import { RoomConfig, HotelGuestBottomSheetProps } from '../../types';
import { defaultGuestSelectionConfig as Constants } from './constants';
import { pd, getColor } from '@airasia-phoenix/react-native/utils/utils';
import { useTranslation } from 'react-i18next';
import PortalWrapperView from '../../components/PortalWrapperView/PortalWrapperView';
import { useListFeatureFlag } from '../../hooks';

type AnchorLayout = { x: number; y: number; width: number; height: number };

const GAP = 4;
const EDGE_PADDING = 8;
const PORTAL_CARD_ID = 'hotelGuestPortalCard';
const GUEST_CARD_WIDTH = 360;

const GuestBottomSheetView = (props: HotelGuestBottomSheetProps) => {
  const stop = useCallback((e?: GestureResponderEvent) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
  }, []);

  const {
    onConfirm,
    bottomSheetViewRef,
    onRequestCloseModal,
    initialRooms,
    isSheetOpen,
    isModalVisible,
    anchorRef,
    config = Constants,
  } = props;

  const isParentModalMode = isModalVisible;

  const { width: windowWidth } = useWindowDimensions();
  const [draftRooms, setDraftRooms] = useState<RoomConfig[]>(initialRooms);
  const { t } = useTranslation('hotels');

  const portalScrollRef = useRef<ScrollView | null>(null);
  const shouldAutoScroll = useRef(false);
  const [anchorLayout, setAnchorLayout] = useState<AnchorLayout | null>(null);

  useEffect(() => {
    if (isSheetOpen || isModalVisible) {
      setDraftRooms([...initialRooms]);
    }
  }, [isSheetOpen, isModalVisible, initialRooms]);

  const handleDraftRoomsChange = useCallback(
    (rooms: RoomConfig[], isAdding?: boolean) => {
      setDraftRooms(rooms);
      if (isParentModalMode) {
        onConfirm(rooms);
      }

      if (isAdding) {
        if (isParentModalMode) {
          const scrollRef = portalScrollRef.current;

          if (scrollRef) {
            setTimeout(() => {
              scrollRef.scrollToEnd({ animated: true });
            }, 100);
          }
        } else if (!isParentModalMode) {
          shouldAutoScroll.current = true;
          setTimeout(() => {
            shouldAutoScroll.current = false;
          }, 200);
        }
      }
    },
    [isParentModalMode, onConfirm],
  );

  const handleConfirm = useCallback(
    (e?: GestureResponderEvent) => {
      stop(e);
      onConfirm(draftRooms);

      if (isParentModalMode) {
        onRequestCloseModal();
      } else {
        bottomSheetViewRef.current?.toggleBottomSheetView();
        onRequestCloseModal();
      }
    },
    [
      bottomSheetViewRef,
      draftRooms,
      isParentModalMode,
      onConfirm,
      onRequestCloseModal,
      stop,
    ],
  );

  const measureAnchor = useCallback(() => {
    if (Platform.OS === 'web') {
      const element = anchorRef?.current as unknown as HTMLElement;
      if (element && element.getBoundingClientRect) {
        const rect = element.getBoundingClientRect();
        setAnchorLayout({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }
    } else {
      anchorRef?.current?.measureInWindow((x, y, width, height) => {
        setAnchorLayout({ x, y, width, height });
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    if (isParentModalMode) {
      measureAnchor();
    }
  }, [isParentModalMode, measureAnchor]);

  const handleReposition = useCallback(() => {
    measureAnchor();
  }, [measureAnchor]);

  useEffect(() => {
    if (!isParentModalMode || Platform.OS !== 'web') return;

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isParentModalMode, handleReposition]);

  useEffect(() => {
    if (!isParentModalMode) return;

    const handler = (e: PointerEvent) => {
      const guestNode = document.getElementById(PORTAL_CARD_ID);
      const childNode = document.getElementById('hotelChildAgePortalCard');

      const target = e.target as Node;

      const isInsideGuest = guestNode && guestNode.contains(target);

      const targetElement = target as Element;
      const isChildAgeBackdrop =
        targetElement?.closest?.('[data-testid$="-backdrop"]') ||
        childNode?.contains(target);

      if (!isInsideGuest && !isChildAgeBackdrop) {
        onRequestCloseModal();
      }
    };

    document.addEventListener('pointerdown', handler, true);

    return () => {
      document.removeEventListener('pointerdown', handler, true);
    };
  }, [isParentModalMode, onRequestCloseModal]);
  const { hotel_enable_portal_usage: isPortalUsageEnabled } =
    useListFeatureFlag();

  const portalCardStyle = useMemo(() => {
    if (!anchorLayout) return {};
    const cardWidth = GUEST_CARD_WIDTH;
    const preferredLeft = anchorLayout.x + anchorLayout.width - cardWidth;

    return {
      position: 'absolute' as const,
      top: anchorLayout.y + anchorLayout.height + GAP,
      left: Math.max(
        EDGE_PADDING,
        Math.min(preferredLeft, windowWidth - cardWidth - EDGE_PADDING),
      ),
      width: cardWidth,
      maxWidth: windowWidth - EDGE_PADDING * 2,
      maxHeight: config.WEB_MAX_HEIGHT,
      borderRadius: 18,
      overflow: 'hidden' as const,
      backgroundColor: getColor(pd.sys.colour.container_none),
      borderWidth: 1,
      borderColor: getColor(pd.sys.colour.outline_medium),
      zIndex: 20,
    };
  }, [anchorLayout, config.WEB_MAX_HEIGHT, windowWidth]);
  const dropDownStyle: ViewStyle = useMemo(() => {
    if (!anchorLayout) return { opacity: 0 };
    const cardWidth = GUEST_CARD_WIDTH;
    const EDGE_PADDING = 16;
    const GAP = 54;

    let layoutLeft = anchorLayout.x;
    let layoutWidth = anchorLayout.width;

    // 1. Locate the precise search widget container using its ID
    if (
      typeof document !== 'undefined' &&
      Platform.OS === 'web' &&
      anchorRef?.current
    ) {
      const anchorEl = anchorRef.current as unknown as HTMLElement;
      const widgetEl = anchorEl.closest('#search-widget-container');

      if (widgetEl) {
        const rect = widgetEl.getBoundingClientRect();
        layoutLeft = rect.left;
        layoutWidth = rect.width;
      }
    }

    // 2. Ensure we respect a minimum padding from the screen edge
    const safeLayoutLeft = layoutLeft + EDGE_PADDING;
    const safeLayoutRight = layoutLeft + layoutWidth - EDGE_PADDING;

    // 3. Translate the widget boundaries to local coordinates relative to the anchor
    const minLocalLeft = safeLayoutLeft - anchorLayout.x;
    const maxLocalLeft = safeLayoutRight - cardWidth - anchorLayout.x;

    // 4. Calculate the ideal right-aligned offset relative to the anchor
    const localDesiredLeft = anchorLayout.width - cardWidth;

    // 5. Clamp the position perfectly
    const left = Math.max(
      minLocalLeft,
      Math.min(localDesiredLeft, maxLocalLeft),
    );
    const top = GAP;

    return {
      position: 'absolute' as const,
      left,
      top,
      width: cardWidth,
      maxWidth: windowWidth - EDGE_PADDING * 2,
      maxHeight: config.WEB_MAX_HEIGHT,
      borderRadius: 18,
      overflow: 'hidden' as const,
      backgroundColor: getColor(pd.sys.colour.container_none),
      borderWidth: 1,
      borderColor: getColor(pd.sys.colour.outline_medium),
      zIndex: 20,
    };
  }, [anchorLayout, anchorRef, config.WEB_MAX_HEIGHT, windowWidth]);

  if (isParentModalMode) {
    if (!anchorLayout) return null;
    return (
      <PortalWrapperView>
        <View style={styles.visualOverlay} />
        <View
          nativeID={PORTAL_CARD_ID}
          style={isPortalUsageEnabled ? portalCardStyle : dropDownStyle}
        >
          <View style={styles.portalContainer}>
            <ScrollView
              ref={portalScrollRef}
              style={styles.portalScroll}
              contentContainerStyle={styles.portalScrollContent}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled
            >
              <GuestSelectionView
                rooms={draftRooms}
                onUpdateRooms={handleDraftRoomsChange}
                config={config}
                isParentModal={true}
              />
            </ScrollView>
          </View>
        </View>
      </PortalWrapperView>
    );
  }

  return (
    <BottomSheetWrapper
      bottomSheetRef={bottomSheetViewRef}
      title={t('searchHotelPage.guestSelection.title')}
      onClose={props.onRequestCloseModal}
      footerHeight={config.GUEST_SHEET_FOOTER_HEIGHT - 30}
      stickyFooter={
        <StickyBottomBarView
          primaryButtonLabel={t('searchHotelPage.guestSelection.confirm')}
          primaryButtonOnPress={handleConfirm}
          testID="guest-bottom-sheet-confirm"
        />
      }
      shouldAutoScroll={shouldAutoScroll}
    >
      <View style={styles.container}>
        <GuestSelectionView
          rooms={draftRooms}
          onUpdateRooms={handleDraftRoomsChange}
          config={config}
          isParentModal={false}
        />
      </View>
    </BottomSheetWrapper>
  );
};

const styles = StyleSheet.create((pd) => ({
  container: {
    flex: 1,
    backgroundColor: getColor(pd.sys.colour.container_none),
  },
  portalContainer: {
    flex: 1,
    paddingHorizontal: pd.ref.space.spacing30.px,
    paddingBottom: pd.ref.space.spacing10.px,
  },
  portalScroll: {
    flexGrow: 0,
    ...(Platform.OS === 'web'
      ? { overscrollBehavior: 'contain' as const }
      : {}),
  },
  portalScrollContent: {
    paddingBottom: pd.ref.space.spacing10.px,
  },
  visualOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}));

export default GuestBottomSheetView;
